/**
 * Wording for third-party fulfilment, phase 2 — one place for every label the
 * catalogue item page and the catalogue editor show, so the glossary in the
 * brief (docs/features/catalogue-third-party-fulfilment/07_phase_2_product_brief.md
 * §19) is the words on screen, verbatim.
 *
 * Headless: no styling, no DOM. Business decisions (availability, the
 * default and its reason, consequences) come from the backend; this only words
 * what it returned.
 */

import { extractFieldErrors } from "../data/validation";
import type {
  CataloguePriceOption,
  CataloguePriceOptions,
  PriceOptionDelivery,
  RouteNarrowing,
  RouteOverrideReason,
} from "../data/supplier-pricing-types";
import {
  IN_HOUSE_ROUTE,
  ROUTE_OVERRIDE_REASONS,
} from "../data/supplier-pricing-types";

/** "In-house · Standard", "Solopress · Cheapest". Staff surfaces only. */
export function priceOptionTitle(o: {
  routeLabel: string;
  tier: string;
}): string {
  return `${o.routeLabel} · ${o.tier}`;
}

/** A quote line's lead time (until N4 replaces it with the stored schedule). */
export function workingDaysLabel(days: number | null): string {
  if (days == null) return "Lead time not given";
  return `${days} working day${days === 1 ? "" : "s"}`;
}

/** The delivery half of an option's second line. */
export function deliveryLabel(
  delivery: PriceOptionDelivery,
  formatPence: (pence: number) => string,
): string {
  switch (delivery.kind) {
    case "priced":
      return `Delivery ${formatPence(delivery.deliveryPricePence)}`;
    case "at_address":
      return "Delivery priced at the address";
    case "not_priced":
      return "Delivery not priced yet";
    case "included":
      return "Delivery included";
    case "none":
      return "No delivery";
  }
}

export function findOption(
  options: CataloguePriceOptions,
  key: string,
): CataloguePriceOption | undefined {
  return options.options.find((o) => o.key === key);
}

/** Is the CSR's choice an override that needs a reason (#2)? The backend says so. */
export function isOverride(options: CataloguePriceOptions): boolean {
  return options.overrideRequirement != null;
}

/**
 * Why Send / Place order cannot go yet, or null: the backend's own prompt for
 * the reason, or for the note a reason like "Other" needs (#2). Reads the
 * preview's `overrideRequirement`; the rule itself is the backend's.
 */
export function overrideBlocker(
  options: CataloguePriceOptions | null | undefined,
  reason: RouteOverrideReason | null,
  note: string,
): string | null {
  const requirement = options?.overrideRequirement;
  if (!requirement) return null;
  if (reason == null) return requirement.reasonPrompt;
  if (requirement.noteRequiredFor.includes(reason) && note.trim() === "")
    return requirement.notePrompt;
  return null;
}

/** The backend's refusal of an override, bound to the reason or the note it names. */
export interface RouteOverrideErrors {
  reason?: string;
  note?: string;
}

/**
 * Read the backend's 400 on a Send / Place order that overrode the default
 * without its reason (#2) — `route_override_reason`, or `route_override_note`
 * for "Other" — so the page shows the backend's words next to the input.
 * Empty for any other failure. The page does not judge the override itself.
 */
export function routeOverrideErrors(err: unknown): RouteOverrideErrors {
  const errors: RouteOverrideErrors = {};
  for (const e of extractFieldErrors(err)) {
    if (e.field === "route_override_reason") errors.reason = e.message;
    if (e.field === "route_override_note") errors.note = e.message;
  }
  return errors;
}

export function overrideReasonLabel(reason: RouteOverrideReason): string {
  return (
    ROUTE_OVERRIDE_REASONS.find((r) => r.value === reason)?.label ?? reason
  );
}

/**
 * What changed between two previews that the CSR should be told about
 * (journey C): the default moved once delivery was priced, or the option they
 * had chosen is no longer on offer.
 */
export type PriceOptionsNotice =
  | { kind: "default_changed"; from: string; to: string }
  | { kind: "choice_dropped"; choice: string; reason: string | null };

export function priceOptionsNotice(
  previous: CataloguePriceOptions | null | undefined,
  next: CataloguePriceOptions | null | undefined,
  chosenKey: string | null,
): PriceOptionsNotice | null {
  if (!previous || !next) return null;
  // A route withheld because its delivery isn't priced has its own warning, with the agreed price (doc 107).
  const isWithheld = (routeCode: string | undefined) =>
    next.unavailable.some(
      (u) => u.routeCode === routeCode && u.isDeliveryNotPriced,
    );
  if (chosenKey && !next.options.some((o) => o.key === chosenKey)) {
    const dropped = previous.options.find((o) => o.key === chosenKey);
    const routeCode = dropped?.routeCode;
    if (isWithheld(routeCode)) return null;
    return {
      kind: "choice_dropped",
      choice: dropped ? priceOptionTitle(dropped) : chosenKey,
      reason:
        next.unavailable.find((u) => u.routeCode === routeCode)?.reason ?? null,
    };
  }
  if (chosenKey == null && previous.defaultKey !== next.defaultKey) {
    const from = findOption(previous, previous.defaultKey);
    const to = findOption(next, next.defaultKey);
    if (
      from &&
      to &&
      previous.isProvisional &&
      !next.isProvisional &&
      !isWithheld(from.routeCode)
    ) {
      return {
        kind: "default_changed",
        from: priceOptionTitle(from),
        to: priceOptionTitle(to),
      };
    }
  }
  return null;
}

/** "In-house only", "Solopress only", "In-house and Solopress", "Every route". */
export function describeRouteNarrowing(
  narrowing: RouteNarrowing | null | undefined,
  labelFor: (routeCode: string) => string,
): string {
  if (narrowing?.pinnedRoute) return `${labelFor(narrowing.pinnedRoute)} only`;
  const allowed = narrowing?.allowedRoutes ?? [];
  if (allowed.length === 0) return "Every route";
  if (allowed.length === 1) return `${labelFor(allowed[0])} only`;
  return allowed.map(labelFor).join(" and ");
}

export function routeLabelFor(
  routeCode: string,
  suppliers: ReadonlyArray<{ routeCode: string; routeLabel: string }>,
): string {
  if (routeCode === IN_HOUSE_ROUTE) return "In-house";
  return (
    suppliers.find((s) => s.routeCode === routeCode)?.routeLabel ?? routeCode
  );
}

/** A narrowing that allows every route, in the one shape the wire and the editor agree on. */
export function isEveryRoute(narrowing: RouteNarrowing | null | undefined) {
  return !narrowing?.pinnedRoute && !(narrowing?.allowedRoutes?.length ?? 0);
}
